import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Trophy, MapPin, Award, ChevronRight, Brain, Heart } from "lucide-react";

export default function Leaderboard() {
    const { contestId } = useParams();
    const [contests, setContests] = useState([]);
    const [selectedContest, setSelectedContest] = useState(contestId || "");
    const [ranking, setRanking] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/contests").then((r) => {
            setContests(r.data.contests || []);
            if (!selectedContest && r.data.contests?.length) {
                setSelectedContest(r.data.contests[0].id);
            }
        });
    }, []); // eslint-disable-line

    useEffect(() => {
        if (!selectedContest) return;
        setLoading(true);
        api.get(`/leaderboard/${selectedContest}`).then((r) => {
            setRanking(r.data.ranking || []);
            setLoading(false);
        });
    }, [selectedContest]);

    const contest = contests.find((c) => c.id === selectedContest);

    return (
        <main className="pt-24 pb-12 min-h-screen">
            <div className="container-x">
                <div className="mb-8 fade-up flex items-end justify-between flex-wrap gap-4">
                    <div>
                        <div className="text-xs uppercase tracking-widest text-[var(--gold-deep)] mb-2">Bảng xếp hạng</div>
                        <h1 className="font-heading text-3xl sm:text-4xl font-bold">Top thí sinh xuất sắc</h1>
                    </div>
                    <select value={selectedContest} onChange={(e) => setSelectedContest(e.target.value)}
                        className="input max-w-md" data-testid="contest-filter">
                        {contests.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
                    </select>
                </div>

                {contest && (
                    <div className="card p-6 mb-6 flex items-center gap-4">
                        <Trophy className="w-10 h-10 text-yellow-400" />
                        <div className="flex-1">
                            <h2 className="font-heading font-semibold text-lg">{contest.title}</h2>
                            <p className="text-sm text-slate-500">{contest.organizer}</p>
                        </div>
                        <span className="badge badge-gold">{ranking.length} bài đã nộp</span>
                    </div>
                )}

                {/* Top 3 podium */}
                {!loading && ranking.length >= 3 && (
                    <div className="grid md:grid-cols-3 gap-4 mb-8">
                        {[ranking[1], ranking[0], ranking[2]].map((r, i) => {
                            const realRank = r.rank;
                            const podiumOrder = [1, 0, 2][i];
                            return (
                                <div key={r.id} className={`card p-6 text-center relative overflow-hidden ${
                                    realRank === 1 ? "border-yellow-400/40 md:order-2 md:-translate-y-2" :
                                    realRank === 2 ? "md:order-1" : "md:order-3"
                                }`}>
                                    <div className={`absolute top-0 left-0 right-0 h-1 ${
                                        realRank === 1 ? "bg-yellow-400" : realRank === 2 ? "bg-slate-300" : "bg-amber-700"
                                    }`} />
                                    <div className={`text-5xl font-heading font-bold mb-3 ${
                                        realRank === 1 ? "text-yellow-400" : realRank === 2 ? "text-slate-600" : "text-amber-600"
                                    }`}>#{realRank}</div>
                                    <div className="w-16 h-16 mx-auto rounded-full bg-[var(--gold)]/10 border border-[var(--gold)]/40 flex items-center justify-center mb-3 overflow-hidden">
                                        {r.user_avatar ? <img src={r.user_avatar} alt="" className="w-full h-full object-cover" /> : <Trophy className="w-7 h-7 text-[var(--gold-deep)]" />}
                                    </div>
                                    <div className="font-heading font-semibold mb-1">{r.user_name}</div>
                                    <div className="text-xs text-slate-500 flex items-center justify-center gap-1"><MapPin className="w-3 h-3" /> {r.user_province || "—"}</div>
                                    <div className="mt-3 text-2xl font-bold text-[var(--gold-deep)]">{(r.final_score ?? r.combined_score)?.toFixed(1) ?? "—"}</div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Full ranking */}
                <div className="card overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-[var(--bg-alt)] text-xs uppercase tracking-widest text-slate-500">
                                <tr>
                                    <th className="p-4 text-left">#</th>
                                    <th className="p-4 text-left">Thí sinh</th>
                                    <th className="p-4 text-left">Tỉnh/Thành</th>
                                    <th className="p-4 text-center">Trắc nghiệm</th>
                                    <th className="p-4 text-center">AI tự luận</th>
                                    <th className="p-4 text-center">BGK</th>
                                    <th className="p-4 text-center">Vote</th>
                                    <th className="p-4 text-right">Tổng điểm</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan={8} className="p-10 text-center text-slate-500">Đang tải…</td></tr>
                                ) : ranking.length === 0 ? (
                                    <tr><td colSpan={8} className="p-10 text-center text-slate-500">Chưa có bài thi nào</td></tr>
                                ) : ranking.map((r) => (
                                    <tr key={r.id} className="border-t border-[var(--border)] hover:bg-[var(--gold)]/5 transition" data-testid={`rank-row-${r.rank}`}>
                                        <td className="p-4 font-mono font-bold">
                                            {r.rank <= 3 ? <span className={r.rank === 1 ? "text-yellow-400" : r.rank === 2 ? "text-slate-600" : "text-amber-600"}>#{r.rank}</span> : `#${r.rank}`}
                                        </td>
                                        <td className="p-4">
                                            <div className="flex items-center gap-2">
                                                <div className="w-8 h-8 rounded-full bg-[var(--gold)]/10 flex items-center justify-center overflow-hidden">
                                                    {r.user_avatar ? <img src={r.user_avatar} className="w-full h-full object-cover" alt="" /> : <span className="text-[var(--gold-deep)] text-xs">{r.user_name?.[0]}</span>}
                                                </div>
                                                <div>
                                                    <div className="text-[var(--navy)] font-medium">{r.user_name}</div>
                                                    <div className="text-xs text-slate-500 font-mono">{r.user_candidate_code}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="p-4 text-slate-600">{r.user_province || "—"}</td>
                                        <td className="p-4 text-center text-slate-600">{r.mcq_score_pct?.toFixed(0) ?? "—"}</td>
                                        <td className="p-4 text-center"><span className="text-[var(--gold-deep)] inline-flex items-center gap-1"><Brain className="w-3 h-3" /> {r.ai_essay_score?.toFixed(0) ?? "—"}</span></td>
                                        <td className="p-4 text-center"><span className="text-yellow-400">{r.jury_essay_score?.toFixed(0) ?? "—"}</span></td>
                                        <td className="p-4 text-center"><span className="inline-flex items-center gap-1 text-pink-400"><Heart className="w-3 h-3" /> {r.votes_count || 0}</span></td>
                                        <td className="p-4 text-right">
                                            <span className="text-lg font-heading font-bold text-[var(--gold-deep)]">{(r.final_score ?? r.combined_score)?.toFixed(1) ?? "—"}</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="mt-6 text-center">
                    <Link to={`/binh-chon`} className="btn-secondary"><Heart className="w-4 h-4" /> Bình chọn bài thi yêu thích <ChevronRight className="w-4 h-4" /></Link>
                </div>
            </div>
        </main>
    );
}
